import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Check, Star, Sparkles } from "lucide-react";

export default function Pricing() {
  const [packages, setPackages] = useState([]);
  const [addons, setAddons] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    Promise.all([api.get("/packages"), api.get("/addons")]).then(([p, a]) => {
      setPackages(p.data);
      setAddons(a.data);
    }).catch(() => {});
  }, []);

  const videoAddons = addons.filter((a) => a.category === "video");
  const audioAddons = addons.filter((a) => a.category === "audio");

  return (
    <div className="bg-black">
      <div className="container-x section">
        <div className="text-center mb-16">
          <div className="eyebrow mb-3">Pricing</div>
          <h1 className="font-heading text-4xl md:text-5xl mb-4">Choose Your Variation Package</h1>
          <p className="text-white/65 max-w-2xl mx-auto">
            Each project includes a creative discovery process. Pay only for the variations you love.
          </p>
        </div>

        {/* Packages */}
        <div className="grid md:grid-cols-3 gap-6 mb-24">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              data-testid={`pricing-pkg-${pkg.key}`}
              className={`relative bg-ink-900 border p-8 flex flex-col transition-all duration-300 ${
                pkg.recommended ? "border-gold shadow-gold scale-100 md:scale-105" : "border-ink-700 hover:border-gold/60"
              }`}
            >
              {pkg.recommended && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-black text-[10px] tracking-[0.25em] uppercase font-bold px-4 py-1 flex items-center gap-1">
                  <Sparkles size={12} /> Recommended
                </div>
              )}
              <div className="eyebrow mb-2">{pkg.name}</div>
              <div className="flex items-baseline gap-2 mb-3">
                <div className="font-heading text-5xl text-white">${pkg.price.toFixed(0)}</div>
                {pkg.savings && <div className="text-gold text-sm font-semibold">{pkg.savings}</div>}
              </div>
              <p className="text-white/65 text-sm mb-6">{pkg.description}</p>
              <ul className="space-y-3 mb-8 flex-1">
                {pkg.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-white/80">
                    <Check size={16} className="text-gold mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => nav(`/order/new?package=${pkg.key}`)}
                data-testid={`pricing-select-${pkg.key}`}
                className={pkg.recommended ? "btn-gold w-full justify-center" : "btn-outline w-full justify-center"}
              >
                Select Package
              </button>
            </div>
          ))}
        </div>

        {/* Video Add-ons */}
        {videoAddons.length > 0 && (
          <div className="mb-20">
            <div className="text-center mb-10">
              <div className="eyebrow mb-2">Video Production Add-Ons</div>
              <h2 className="font-heading text-3xl">Turn Your Song Into A Marketing Asset</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {videoAddons.map((a) => (
                <div key={a.id} className="card p-7" data-testid={`addon-${a.key}`}>
                  <div className="flex items-baseline justify-between mb-3">
                    <h3 className="font-heading text-xl text-white">{a.name}</h3>
                    <div className="text-gold font-heading text-2xl">${a.price.toFixed(0)}</div>
                  </div>
                  <p className="text-white/60 text-sm mb-5">{a.description}</p>
                  <ul className="space-y-2 text-sm text-white/80">
                    {a.features.map((f) => (
                      <li key={f} className="flex items-center gap-2"><Star size={12} className="text-gold" /> {f}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Audio Add-ons */}
        {audioAddons.length > 0 && (
          <div>
            <div className="text-center mb-10">
              <div className="eyebrow mb-2">Audio Add-Ons</div>
              <h2 className="font-heading text-3xl">Extra Versions & Formats</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {audioAddons.map((a) => (
                <div key={a.id} className="bg-ink-900 border border-ink-700 p-5 hover:border-gold/60 transition-colors" data-testid={`addon-${a.key}`}>
                  <div className="flex items-baseline justify-between mb-2">
                    <h3 className="font-heading text-base text-white">{a.name}</h3>
                    <div className="text-gold font-semibold">${a.price.toFixed(0)}</div>
                  </div>
                  <p className="text-white/55 text-xs">{a.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="text-center mt-16">
          <Link to="/order/new" className="btn-gold" data-testid="pricing-start-project">Start Your Project</Link>
        </div>
      </div>
    </div>
  );
}
